/**
 * Copy, with the fallback the async API needs.
 *
 * `navigator.clipboard` is undefined on an insecure origin and rejects when the document is not
 * focused, which is exactly what happens if the pointer left the window between the click and the
 * promise. The `execCommand` path is deprecated and still the only thing that works in both cases,
 * so it stays as the catch rather than as the primary.
 *
 * Silent on success by design: `feedback-progress-in-control-not-toasts` — a copy that worked needs
 * no announcement, and the glyph the user just pressed is the receipt.
 */
export async function copyText(text: string): Promise<boolean> {
  if (!text) return false;

  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    /* fall through to the textarea */
  }

  try {
    const holder = document.createElement('textarea');
    holder.value = text;
    // Off-screen rather than hidden: `display: none` and `visibility: hidden` both make the
    // selection unreachable, so the copy silently does nothing.
    holder.style.position = 'fixed';
    holder.style.top = '-1000px';
    holder.setAttribute('readonly', '');
    document.body.appendChild(holder);
    holder.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(holder);
    return ok;
  } catch {
    return false;
  }
}
